/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Huu Khang Nguyen
 * Created: 03/02/22
 */

import { useAuth } from '@/components/auth/useAuth';
import { Workplan } from '@/wpt-lib/interfaces/Workplan';
import React from 'react';
import { Comment } from '@/wpt-lib/interfaces/Comment';
import SingleComment from '@/components/pages/workplan/comment/SingleComment';
import { Flex, VStack, Text } from '@chakra-ui/react';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { SelectedCommentAction } from '@/components/pages/workplan/comment/CommentsTile';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { EntityChange } from '@/wpt-lib/interfaces/EntityChange';

export interface CommentsListProps<T> {
    workplan: Workplan;
    comments?: (T & EntityChange)[];
    selectedComment?: SelectedCommentAction<T>;
    setSelectedComment?: (comment: SelectedCommentAction<T> | undefined) => void;
    editError?: unknown;
    deleteError?: unknown;
    deletedComment?: T;
    onDelete?: (arg: T) => void;
    onEdit?: (arg: T, newComment: string) => void;
    currentAssignment?: Assignment;
    system?: T[];
    filter?: (commment: Comment) => boolean;
    placeholder?: string;
}

const CommentsList = <T extends Comment & Partial<EntityChange>>({
    currentAssignment,
    editError,
    selectedComment,
    setSelectedComment,
    deleteError,
    comments,
    system,
    onDelete,
    onEdit,
    filter,
    placeholder = 'No comments',
}: CommentsListProps<T>) => {
    const { authState } = useAuth();
    const list = [...(comments ?? []), ...(system?.map((s) => ({ ...s, system: true })) ?? [])].filter((c) =>
        filter ? filter(c) : true,
    );

    return (
        <Flex py={4} overflowY={'auto'}>
            <VStack w={'full'} spacing={2} alignItems={'flex-start'}>
                {list.length === 0 && (
                    <Text color={'gray.700'} fontStyle="italic">
                        {placeholder}
                    </Text>
                )}
                {list
                    .sort((a, b) => new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf())
                    .map((comment, i) => {
                        const isAuthor = comment.author?.user?.id === authState?.claims?.user?.id;
                        const canEdit = isAuthor && !comment.approved;
                        return (
                            <React.Fragment key={i}>
                                <SingleComment
                                    isChanged={!!comment.changed}
                                    comment={comment}
                                    editError={editError}
                                    onEdit={canEdit ? onEdit : undefined}
                                    onDelete={canEdit ? onDelete : undefined}
                                    currentAssignment={currentAssignment}
                                    selectedComment={selectedComment}
                                    setSelectedComment={setSelectedComment}
                                />
                                {!!deleteError &&
                                    selectedComment?.comment?.id === comment.id &&
                                    selectedComment?.type === 'deleteError' && (
                                        <SimpleAlert variant={'left-accent'}>
                                            <Text>
                                                Cannot delete this comment, please refresh the page and try again
                                            </Text>
                                        </SimpleAlert>
                                    )}
                            </React.Fragment>
                        );
                    })}
            </VStack>
        </Flex>
    );
};
export default CommentsList;
